/**
 * WiZi Quiz State Management
 * Handles session storage for quiz state persistence
 */

export interface WiziQuestionState {
  status: 'not_attempted' | 'answered' | 'skipped' | 'marked_review' | 'answered_marked_review';
  user_answer: string | null;
  time_spent: number;
  visited: boolean;
}

export interface WiziQuizSessionState {
  attempt_id: number;
  wizi_quiz_id: number;
  current_question_index: number;
  time_remaining: number;
  question_states: WiziQuestionState[];
  last_saved: number;
  total_time_limit: number;
}

const STORAGE_KEY = 'wizi_quiz_session';

/**
 * Save quiz state to session storage
 */
export const saveWiziQuizState = (state: WiziQuizSessionState): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    console.log('WiZi Quiz state saved:', state.attempt_id);
  } catch (error) {
    console.error('Failed to save WiZi Quiz state:', error);
  }
};

/**
 * Load quiz state from session storage
 */
export const loadWiziQuizState = (attempt_id: number): WiziQuizSessionState | null => {
  try {
    const data = sessionStorage.getItem(STORAGE_KEY);
    if (!data) return null;

    const state: WiziQuizSessionState = JSON.parse(data);
    
    // Verify it's for the same attempt
    if (state.attempt_id !== attempt_id) {
      console.log('Saved state is for different attempt, clearing...');
      clearWiziQuizState();
      return null;
    }

    console.log('WiZi Quiz state loaded:', state.attempt_id);
    return state;
  } catch (error) {
    console.error('Failed to load WiZi Quiz state:', error);
    return null;
  }
};

/**
 * Clear quiz state from session storage
 */
export const clearWiziQuizState = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    console.log('WiZi Quiz state cleared');
  } catch (error) {
    console.error('Failed to clear WiZi Quiz state:', error);
  }
};

/**
 * Check if saved state exists
 */
export const hasWiziQuizState = (): boolean => {
  return sessionStorage.getItem(STORAGE_KEY) !== null;
};

/**
 * Throttled save function to prevent excessive writes
 */
let saveTimeout: NodeJS.Timeout | null = null;

export const saveWiziQuizStateThrottled = (state: WiziQuizSessionState, delay: number = 1000): void => {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }

  saveTimeout = setTimeout(() => {
    saveWiziQuizState(state);
    saveTimeout = null;
  }, delay);
};
